import { create } from "zustand";
import {
  accProjectStorageKey,
  type PackageScanMode,
  readSrProfile,
  writeSrProfile,
} from "../lib/srProfileDb";

interface SrProfileState {
  accProjectKey: string | null;
  permittingAuthorityId: string;
  packageScanMode: PackageScanMode;
  submissionFolderId: string | null;
  submissionFolderLabel: string | null;
  siteAddressOverride: string;

  bindAccProject: (hubId: string, projectId: string) => void;
  setPermittingAuthorityId: (id: string) => void;
  setPackageScanMode: (mode: PackageScanMode) => void;
  setSubmissionFolder: (folderId: string | null, label: string | null) => void;
  setSiteAddressOverride: (line: string) => void;
}

const DEFAULT_AUTHORITY = "us-ca-la-county";

function persist(state: SrProfileState): void {
  if (!state.accProjectKey) return;
  writeSrProfile({
    accProjectKey: state.accProjectKey,
    permittingAuthorityId: state.permittingAuthorityId,
    packageScanMode: state.packageScanMode,
    submissionFolderId: state.submissionFolderId ?? undefined,
    submissionFolderLabel: state.submissionFolderLabel ?? undefined,
    siteAddressOverride: state.siteAddressOverride || undefined,
    updatedAt: new Date().toISOString(),
  });
}

export const useSrProfileStore = create<SrProfileState>((set, get) => ({
  accProjectKey: null,
  permittingAuthorityId: DEFAULT_AUTHORITY,
  packageScanMode: "whole-project",
  submissionFolderId: null,
  submissionFolderLabel: null,
  siteAddressOverride: "",

  bindAccProject: (hubId, projectId) => {
    const key = accProjectStorageKey(hubId, projectId);
    const saved = readSrProfile(key);
    set({
      accProjectKey: key,
      permittingAuthorityId: saved?.permittingAuthorityId ?? DEFAULT_AUTHORITY,
      packageScanMode: saved?.packageScanMode ?? "whole-project",
      submissionFolderId: saved?.submissionFolderId ?? null,
      submissionFolderLabel: saved?.submissionFolderLabel ?? null,
      siteAddressOverride: saved?.siteAddressOverride ?? "",
    });
  },

  setPermittingAuthorityId: id => {
    set({ permittingAuthorityId: id });
    persist(get());
  },

  setPackageScanMode: mode => {
    set({
      packageScanMode: mode,
      ...(mode === "whole-project"
        ? { submissionFolderId: null, submissionFolderLabel: null }
        : {}),
    });
    persist(get());
  },

  setSubmissionFolder: (folderId, label) => {
    set({
      submissionFolderId: folderId,
      submissionFolderLabel: label,
      packageScanMode: folderId ? "dedicated-folder" : get().packageScanMode,
    });
    persist(get());
  },

  setSiteAddressOverride: line => {
    set({ siteAddressOverride: line });
    persist(get());
  },
}));
